import { Col, FormGroup, Input, Label, Row } from "reactstrap";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import { useCallback, useEffect, useState } from "react";
import { get, post } from "../../../../utils/helpers/api";
import { useNavigate, useParams } from "react-router-dom";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import { useTranslation } from "react-i18next";
import { useBranch } from "../../../../context/branch";

const EMPTY_FORM = {
  code: '',
  name: '',
  legal_name: '',
  email: '',
  country_code: '',
  phone_number: '',
  street_address: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
  tax_id: '',
  registration_number: '',
  nis: '',
  tax_article: '',
  is_active: true,
};

function CreateBranchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { refresh } = useBranch();

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [codeLocked, setCodeLocked] = useState(false);

  const isEdit = !!id;

  const loadBranch = useCallback(async () => {
    if (!isEdit) return;

    setLoading(true);
    const data = await get(`/branches/view/${id}`);

    if (data?.success) {
      const branch = data.branch;
      setForm({ ...EMPTY_FORM, ...Object.fromEntries(
        Object.keys(EMPTY_FORM).map((key) => [key, branch[key] ?? EMPTY_FORM[key]])
      ) });

      // Once a branch has issued documents its code is frozen server-side,
      // because the code is embedded in every number it has already produced.
      setCodeLocked((branch.warehouses ?? []).length > 0 && !!branch.is_default);
    }

    setLoading(false);
  }, [id, isEdit]);

  useEffect(() => {
    loadBranch();
  }, [loadBranch]);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const save = async () => {
    setLoading(true);
    setErrors({});
    setSaveError(null);

    const endpoint = isEdit ? `/branches/update/${id}` : `/branches/store`;
    const data = await post(endpoint, form);

    if (data?.success) {
      // The switcher caches the branch list, so it has to hear about this.
      await refresh();
      navigate('/settings/branches');
    } else if (data?.errors) {
      setErrors(data.errors);
    } else {
      setSaveError(data?.message ?? t('common.somethingWentWrong'));
    }

    setLoading(false);
  };

  const field = (name, label, type = 'text', extra = {}) => (
    <FormGroup>
      <Label>{label}: </Label>
      <Input
        type={type}
        value={form[name] ?? ''}
        onChange={(e) => update(name, e.target.value)}
        {...extra}
      />
      {errors[name] && <div className="text-danger small mt-1">{errors[name][0]}</div>}
    </FormGroup>
  );

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={isEdit ? t('branches.editBranch') : t('branches.newBranch')}>
          <DefaultButton
            color="primary"
            size="sm"
            onClick={save}
            loading={loading}
            disabled={!form.code.trim() || !form.name.trim() || loading}
          >
            {t('common.save')}
          </DefaultButton>
        </PageSubHeader>

        <div className="subheader-page-content">
          {saveError && <div className="alert alert-danger">{saveError}</div>}

          <Row>
            <Col md={3}>
              <FormGroup>
                <Label>{t('common.code')}: </Label>
                <Input
                  type="text"
                  value={form.code}
                  disabled={codeLocked}
                  onChange={(e) => update('code', e.target.value.toUpperCase())}
                  placeholder={t('branches.codePlaceholder')}
                />
                <div className="text-muted small mt-1">{t('branches.codeHint')}</div>
                {errors.code && <div className="text-danger small mt-1">{errors.code[0]}</div>}
              </FormGroup>
            </Col>
            <Col md={5}>{field('name', t('common.name'))}</Col>
            <Col md={4}>{field('legal_name', t('branches.legalName'))}</Col>
          </Row>

          <Row>
            <Col md={4}>{field('email', t('common.email'), 'email')}</Col>
            <Col md={2}>{field('country_code', t('branches.countryCode'))}</Col>
            <Col md={3}>{field('phone_number', t('contact.phone'))}</Col>
            <Col md={3}>{field('country', t('address.country'))}</Col>
          </Row>

          <Row>
            <Col md={6}>{field('street_address', t('address.street'))}</Col>
            <Col md={2}>{field('city', t('address.city'))}</Col>
            <Col md={2}>{field('state', t('address.state'))}</Col>
            <Col md={2}>{field('postal_code', t('address.zipCode'))}</Col>
          </Row>

          <Row>
            <Col md={3}>{field('tax_id', t('branches.taxId'))}</Col>
            <Col md={3}>{field('registration_number', t('branches.registrationNumber'))}</Col>
            <Col md={3}>{field('nis', t('branches.nis'))}</Col>
            <Col md={3}>{field('tax_article', t('branches.taxArticle'))}</Col>
          </Row>

          <FormGroup check className="mb-3">
            <Input
              type="checkbox"
              id="branch-is-active"
              checked={!!form.is_active}
              onChange={(e) => update('is_active', e.target.checked)}
            />
            <Label check for="branch-is-active">{t('common.active')}</Label>
          </FormGroup>
        </div>
      </div>
    </SidebarLayout>
  );
}

export default CreateBranchPage;
